import React, {forwardRef, MouseEventHandler, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import type YouTubePlayer from "yt-player";
import youTubePlayer from 'yt-player';
import classnames from "classnames";
import {ReactComponent as Play} from "../../../../assets/svgs/video/play.svg";
import {ReactComponent as Pause} from "../../../../assets/svgs/video/pause.svg";
import {ReactComponent as Mute} from "../../../../assets/svgs/video/mute.svg";
import {ReactComponent as Unmute} from "../../../../assets/svgs/video/unmute.svg";
import {ReactComponent as Fullscreen} from "../../../../assets/svgs/video/full-screen.svg";
import {Box, Fade, Theme} from "@mui/material";
import type {VideoPlayerRef, VideoPlayerTypes} from "../index";
import useIsMounted from "../../../hooks/use-is-mounted";

export interface YoutubeVideoPlayerProps {
    muted: boolean,
    noControls: boolean,
    id: string,
    videoId: string,
    title: string,
    autoPlay: boolean,
    type: VideoPlayerTypes,
    className?: string,
}

interface YoutubeVideoPlayerState {
    paused: boolean,
    startedToPlay: boolean,
    duration: number,
    currentTime: number,
    muted: boolean,
    loaded: boolean,
    unplayable: boolean,
}

const initialVideoPlayerState: YoutubeVideoPlayerState = {
    paused: true,
    duration: -1,
    startedToPlay: false,
    currentTime: 0,
    muted: true,
    loaded: false,
    unplayable: false,
}

const YoutubeVideoPlayer = forwardRef<VideoPlayerRef, YoutubeVideoPlayerProps>((props, ref) => {
    const [state, setState] = useState<YoutubeVideoPlayerState>(initialVideoPlayerState);
    const player = useRef<YouTubePlayer>();
    const isMounted = useIsMounted();

    /**
     * Plays the current video of the player
     */
    const play = useCallback<VoidCallback>(() => {
        player.current?.play();
        setState(prevState => ({
            ...prevState,
            progress: player.current?.getProgress() ?? 0,
            startedToPlay: true,
            paused: false,
        }))
    }, [])

    /**
     * Pauses the current video of the player
     */
    const pause = useCallback<VoidCallback>(() => {
        setState(prevState => ({...prevState, paused: true}));
        player.current?.pause();
    }, [])

    /**
     * Mutes the video of the player
     */
    const mute = useCallback<VoidCallback>(() => {
        setState(prevState => ({...prevState, muted: true}));
        player.current?.mute();
    }, [])

    /**
     * Un-Mutes the video of the player
     */
    const unmute = useCallback<VoidCallback>(() => {
        setState(prevState => ({...prevState, muted: false}));
        player.current?.unMute();
    }, [])

    /**
     * Makes the video of the player fullscreen
     */
    const fullscreen = useCallback<VoidCallback>(() => {
        const iframe = (player.current as any)?._player?.getIframe();
        if (!iframe)
            return;
        const requestFullScreen = iframe.requestFullscreen || iframe.mozRequestFullScreen || iframe.webkitRequestFullScreen;
        if (requestFullScreen) {
            requestFullScreen.bind(iframe)();
        }
    }, []);

    /**
     * Fetches the current time of the video.
     */
    const timestamp = useCallback<VoidCallback>(() => player.current?.getCurrentTime(), [])

    /**
     * Seeks the video to the given time.
     */
    const seek = useCallback<(t: number) => void>((time) => player.current?.seek(time), [])

    /**
     * Attaches the video player functionalities to the given ref so that the parent components can directly use them.
     */
    useImperativeHandle(ref, () => ({
        pause,
        play,
        mute,
        unmute,
        fullscreen,
        timestamp,
        seek,
        getPlayerInstance: () => [true, player.current],
    }) as VideoPlayerRef, [pause, play, mute, unmute, fullscreen, timestamp, seek])

    /**
     * Listens to the changes in the paused state and with each change:
     * if the state is playing, then creates an interval that will track the progression of the time and video's
     * duration.
     * Removes the interval before the next call of this listener.
     */
    useEffect(() => {
        let timer: number;
        if (!state.paused) {
            timer = window.setInterval(() => {
                if (!isMounted()) return;
                setState(prevState => ({
                    ...prevState,
                    currentTime: player.current?.getCurrentTime() ?? 0,
                    duration: player.current?.getDuration() ?? -1,
                }))
            }, 1000)
        } else {
            return () => {
                timer && clearInterval(timer)
            };
        }
    }, [state.paused, isMounted])

    /**
     * Listens for the changes in the ref and with each change:
     * if the ref exists, instantiates the current player
     */
    useEffect(() => {
        if (player.current)
            return;
        player.current = new youTubePlayer(`#${props.id}`, {
            autoplay: props.autoPlay,
            captions: undefined,
            controls: false,
            keyboard: true,
            fullscreen: true,
            annotations: false,
            modestBranding: true,
            related: false,
            timeupdateFrequency: 1000,
            playsInline: true,
            start: 0,
        });
    }, [props.id, props.autoPlay, props.videoId]);

    /**
     * Makes the video unplayable so that the play button gets removed from the iframe.
     * @param videoId the video id that could not be played
     */
    const onVideoUnPlayable = useCallback<VoidCallback>(() => {
        setState(prevState => ({...prevState, unplayable: true}))
    }, []);

    /**
     * Listens to the changes in videoId and playerRef and with each change:
     * if they both exist and the player is instantiated, then loads the video with the current videoId
     */
    useEffect(() => {
        if (!props.videoId || !player.current)
            return;
        player.current?.load(props.videoId, props.autoPlay);
        setState({
            paused: true,
            startedToPlay: false,
            duration: -1,
            currentTime: 0,
            muted: player.current?.isMuted() || props.muted,
            loaded: true,
            unplayable: false,
        })
        if (props.muted) {
            mute();
        }
        player.current?.on('paused', pause)
        player.current?.on('playing', play)
        player.current?.on('unplayable', onVideoUnPlayable)
        return () => {
            player.current?.off('paused', pause);
            player.current?.off('playing', play);
            player.current?.off('unplayable', onVideoUnPlayable);
        }
    }, [props.videoId, player, pause, play, onVideoUnPlayable, props.autoPlay, props.muted, mute])


    /**
     * Handles the click event on the progress bar.
     * @param e {MouseEvent} the click event
     */
    const onProgressClicked = useCallback<MouseEventHandler<HTMLProgressElement>>((e) => {
        setState(prevState => {
            const {left, width} = (e.target as HTMLProgressElement).getBoundingClientRect();
            const progress = (e.clientX - left) / width;
            const time = progress * prevState.duration;
            player.current?.seek(time);
            return {...prevState, currentTime: time}
        });
    }, []);

    /**
     * The styles of the component.
     */
    const sx = useCallback<ThemeMixin<Theme>>((theme) => ({
        '& .progress': {
            color: theme.palette.primary.main,
            accentColor: theme.palette.primary.main,
            '&::-webkit-progress-bar': {
                backgroundColor: theme.palette.common.white,
            },
            '&::-webkit-progress-inner-element': {
                backgroundColor: theme.palette.common.white,
            },
            '&::-webkit-progress-value': {
                backgroundColor: theme.palette.primary.main,
            },
            '&::-moz-progress-bar': {
                backgroundColor: theme.palette.primary.main,
            },
        },
    }), [])

    const showPlayButton = useMemo(() => {
        return (props.autoPlay && !state.startedToPlay) || (!props.autoPlay && (state.duration === -1) && (!state.unplayable));
    }, [props.autoPlay, state.duration, state.startedToPlay, state.unplayable]);

    return (
        <>
            <Box
                sx={sx}
                className={classnames('video-container-layout', props.className, {'active': state.duration !== -1, [props.type]: true})}
            >
                <div className={'video-container'}>
                    <Fade in={showPlayButton && !props.noControls}>
                        <div className={classnames('play-button-container')}>
                            <button className={'play-button'} onClick={play}>
                                <Play/>
                            </button>
                        </div>
                    </Fade>
                    <div
                        id={props.id}
                        title={props.title}
                    />
                </div>
                <Fade in={state.duration !== -1 && !props.noControls} mountOnEnter unmountOnExit>
                    <div className={classnames('video-controls', {[props.type]: true})}>
                        <div className={classnames('inner')}>
                            {
                                state.paused
                                    ? <Play className={'video-icon'} onClick={play}/>
                                    : <Pause className={'video-icon'} onClick={pause}/>
                            }
                            <progress
                                className={'progress'}
                                value={state.currentTime?.toString()}
                                onClick={onProgressClicked}
                                max={state.duration}
                            />
                            {
                                state.muted
                                    ? <Unmute className={'video-icon'} onClick={unmute}/>
                                    : <Mute className={'video-icon'} onClick={mute}/>
                            }
                            <Fullscreen className={'video-icon'} onClick={fullscreen}/>
                        </div>
                    </div>
                </Fade>
            </Box>
        </>
    )
})

export default YoutubeVideoPlayer;
